import { Children, isValidElement, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function textOf(node) {
  if (node == null || node === false || node === true) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement(node)) return textOf(node.props.children);
  return '';
}

function readOptions(children) {
  const options = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child) || child.type !== 'option') return;
    const label = textOf(child.props.children).replace(/\s+/g, ' ').trim();
    const raw = child.props.value;
    const value = raw == null ? label : String(raw);
    options.push({
      value,
      label: label || value,
      disabled: Boolean(child.props.disabled),
    });
  });
  return options;
}

function matchesQuery(label, query) {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) return true;
  return label.toLocaleLowerCase().includes(needle);
}

/**
 * Drop-in select whose menu includes a case-insensitive search field.
 * `onChange` receives an event with `target.value`, same as a native select.
 */
export function SearchableSelect({
  id,
  name,
  value,
  onChange,
  children,
  className = '',
  style,
  disabled = false,
  required = false,
  autoFocus = false,
  title,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}) {
  const options = readOptions(children);
  const current = value == null ? '' : String(value);
  const selected = options.find((option) => option.value === current);
  const listId = useId();
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const filtered = options.filter((option) => matchesQuery(option.label, query));
  const activeIndex = filtered.length ? Math.min(active, filtered.length - 1) : 0;

  const close = (focusTrigger = false) => {
    setOpen(false);
    setQuery('');
    if (focusTrigger) triggerRef.current?.focus();
  };

  const choose = (option) => {
    if (!option || option.disabled) return;
    onChange?.({ target: { value: option.value }, currentTarget: { value: option.value } });
    close(true);
  };

  useEffect(() => {
    if (autoFocus) triggerRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (!open) return undefined;
    const frame = requestAnimationFrame(() => searchRef.current?.focus());
    const onPointer = (event) => {
      const target = event.target;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
      setQuery('');
    };
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      close(true);
    };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex, query]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const update = () => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;
      const rect = trigger.getBoundingClientRect();
      const gap = 4;
      const width = Math.min(Math.max(rect.width, 220), window.innerWidth - 16);
      let left = rect.left;
      if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - 8 - width);
      const menuHeight = menu.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const openUp = menuHeight > spaceBelow && spaceAbove > spaceBelow;
      menu.style.width = `${width}px`;
      menu.style.left = `${left}px`;
      menu.style.top = openUp
        ? `${Math.max(8, rect.top - gap - menuHeight)}px`
        : `${rect.bottom + gap}px`;
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, query, filtered.length]);

  const onSearchKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => Math.min(filtered.length - 1, index + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(0, index - 1));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(Math.max(filtered.length - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      choose(filtered[activeIndex]);
    }
  };

  const menu = open ? (
    <div ref={menuRef} className="searchable-select-menu" role="presentation">
      <input
        ref={searchRef}
        className="searchable-select-search"
        value={query}
        placeholder="Search…"
        aria-label={ariaLabel ? `Search ${ariaLabel}` : 'Search options'}
        aria-controls={listId}
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => {
          setQuery(event.target.value);
          setActive(0);
        }}
        onKeyDown={onSearchKeyDown}
      />
      <div className="searchable-select-list" id={listId} role="listbox" aria-label={ariaLabel}>
        {filtered.length ? filtered.map((option, index) => (
          <button
            key={`${option.value}-${index}`}
            type="button"
            role="option"
            className={[
              'searchable-select-option',
              index === activeIndex ? 'is-active' : '',
              option.value === current ? 'is-selected' : '',
            ].filter(Boolean).join(' ')}
            aria-selected={option.value === current}
            disabled={option.disabled}
            data-active={index === activeIndex ? 'true' : undefined}
            onMouseEnter={() => setActive(index)}
            onClick={() => choose(option)}
          >
            {option.label}
          </button>
        )) : <p className="searchable-select-empty">No matches</p>}
      </div>
    </div>
  ) : null;

  return (
    <div ref={rootRef} className={['searchable-select', open ? 'is-open' : '', className].filter(Boolean).join(' ')} style={style}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={['searchable-select-trigger', className].filter(Boolean).join(' ')}
        disabled={disabled}
        title={title}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={(event) => {
          event.stopPropagation();
          if (disabled) return;
          if (open) close();
          else {
            const index = options.findIndex((option) => option.value === current);
            setActive(index >= 0 ? index : 0);
            setQuery('');
            setOpen(true);
          }
        }}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const index = options.findIndex((option) => option.value === current);
            setActive(index >= 0 ? index : 0);
            setOpen(true);
          }
        }}
      >
        <span className="searchable-select-value">{selected?.label || current || 'Select…'}</span>
      </button>
      <input
        className="searchable-select-native"
        tabIndex={-1}
        aria-hidden="true"
        name={name}
        value={current}
        required={required}
        disabled={disabled}
        onChange={() => {}}
        onFocus={() => {
          if (!disabled) setOpen(true);
        }}
      />
      {menu && typeof document !== 'undefined' ? createPortal(menu, document.body) : null}
    </div>
  );
}
